import React from 'react'
import { useHistory } from 'react-router-dom'
import { Box, Grid, InputAdornment, Paper, Tooltip, Typography } from '@mui/material'

import TextField from '@mui/material/TextField'
import SearchIcon from '@mui/icons-material/Search'
import Button from '@mui/material/Button'
import { StartOutlined } from '@mui/icons-material'
import { DataGrid, type GridColumns, type GridValueGetterParams } from '@mui/x-data-grid'

import { useSearchKycArchiveQuery } from '../../../api-services/kyc-archive/kycArchiveApi'
import { RtkQueryHelper } from '../../../api-services/shared/RtkQueryHelper'
import LoadingComponent from '../../components/ui-widgets/LoadingComponent'
import ErrorComponent from '../../components/ui-widgets/ErrorComponent'
import { CountryChip } from '../../components/chips/CountryChip'
import { KycArchiveResultChip } from './KycArchiveResultChip'
import { KycArchivePartialRecordChip } from './KycArchivePartialRecordChip'
import { useAppDispatch, useAppSelector } from '../../../app/hooks'
import {
  selectCurrentApplicationId,
  selectDataGridPage,
  selectPageSize,
  selectSearchText,
  setDataGridPage,
  setSearchText
} from './kycArchiveSlice'
import { formatDateTimeAsApiFormat } from '../../../utility/formatters/DateFormatter'

const KycArchiveSearchPage: React.FC = () => {
  const history = useHistory()
  const dispatch = useAppDispatch()

  const searchText: string = useAppSelector<string>(selectSearchText)
  const page: number = useAppSelector<number>(selectDataGridPage)
  const pageSize: number = useAppSelector<number>(selectPageSize)
  const currentApplicationId = useAppSelector<string | null>(selectCurrentApplicationId)

  const skipQuery = searchText.length < 3
  const searchApplicationsApiResult = useSearchKycArchiveQuery({ q: searchText, page, pageSize }, { skip: skipQuery })

  const goTo = (location: string): void => {
    history.push(location)
  }

  const handleSelectedApplication = (applicationId: string): void => {
    goTo(`/app/kyc-archive/${applicationId}`)
  }

  const handleChangePage = (newPage: number): void => {
    dispatch(setDataGridPage(newPage))
  }

  const handleSearchQueryChange = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const searchQuery: string = event.target.value
    if (searchQuery.length >= 3 || searchQuery.length === 0) {
      dispatch(setSearchText(searchQuery))
    }
  }

  const getApplicationId = (params: GridValueGetterParams): string => {
    return params.row.applicationId
  }

  const columns: GridColumns = [
    {
      field: 'lastName',
      headerName: 'Last Name',
      flex: 35,
      editable: false,
      renderCell: params => (
          <Tooltip title={params.value ?? '-'}><p>{params.value ?? '-'}</p></Tooltip>
      )
    },
    {
      field: 'firstName',
      headerName: 'First Name',
      flex: 30,
      editable: false,
      renderCell: params => (
          <Tooltip title={params.value ?? '-'}><p>{params.value ?? '-'}</p></Tooltip>
      )
    },
    {
      field: 'email',
      headerName: 'Email Address',
      flex: 55,
      editable: false,
      renderCell: params => (
          <Tooltip title={params.value ?? '-'}><p>{params.value ?? '-'}</p></Tooltip>
      )
    },
    {
      field: 'dateOfBirth',
      headerName: 'Date of Birth',
      flex: 30,
      editable: false,
      valueFormatter: params => (params.value != null ? String(params.value).substring(0, 10) : '-')
    },
    {
      field: 'country',
      headerName: 'Country',
      flex: 22,
      editable: false,
      renderCell: params => (
          <CountryChip countryCode={params.value}/>
      )
    },
    {
      field: 'result',
      headerName: 'Result',
      flex: 28,
      editable: false,
      renderCell: params => (
          <>
            <KycArchiveResultChip result={params.value}/>
            {params.row.isReconstructed === true && (
              <Box sx={{ ml: 1 }}><KycArchivePartialRecordChip/></Box>
            )}
          </>
      )
    },
    {
      field: 'lastUpdate',
      headerName: 'Last Update',
      flex: 38,
      editable: false,
      valueFormatter: params => (params.value != null ? formatDateTimeAsApiFormat(new Date(params.value as string)) : '-')
    },
    { field: 'imageCount', headerName: 'Images', flex: 18, editable: false, align: 'right' },
    { field: 'attachmentCount', headerName: 'Files', flex: 18, editable: false, align: 'right' },
    {
      field: 'openApplication',
      headerName: '',
      flex: 15,
      valueGetter: getApplicationId,
      renderCell: params => (
          <div>
              <Button variant="text" onClick={() => { handleSelectedApplication(params.value) }}>
                <StartOutlined />
              </Button>
            </div>
      )
    }
  ]

  return (
    <React.Fragment>
      <Grid container>
        <Grid item lg={12}>
          <Grid container sx={{ mb: 4 }} spacing={3}>
            <Grid item lg={6}>
              <Typography variant={'h5'}>KYC Archive (Acuant)</Typography>
            </Grid>
            <Grid item container lg={6} spacing={2} direction="row" alignItems="right" justifyContent="right">
            </Grid>
          </Grid>
          <Paper>
            <Box sx={{ p: 4, mt: 3 }}>
              <TextField
                label="Enter first name, last name, email address, national id or Acuant application id (minimum 3 characters)."
                type='search'
                defaultValue={searchText}
                sx={{ width: '100%', mb: 2 }}
                placeholder="Email Address"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <SearchIcon />
                    </InputAdornment>
                  )
                }}
                onChange={handleSearchQueryChange}
              />
              {searchApplicationsApiResult.isLoading && <LoadingComponent />}
              {(searchApplicationsApiResult.error != null) && <ErrorComponent message={RtkQueryHelper.mapResponseError(searchApplicationsApiResult.error)} />}
              { searchApplicationsApiResult.isSuccess && (
              <DataGrid rows={searchApplicationsApiResult.data.rows}
                        columns={columns}
                        getRowId={row => row.applicationId}
                        autoHeight={true}
                        disableSelectionOnClick
                        getRowClassName={params => {
                          return currentApplicationId === params.row.applicationId ? 'Mui-selected' : ''
                        }}
                        paginationMode='server'
                        rowCount={searchApplicationsApiResult.data.total}
                        loading={searchApplicationsApiResult.isFetching}
                        pageSize={pageSize}
                        page={page}
                        onPageChange={handleChangePage}
                        rowsPerPageOptions={[pageSize]} />
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </React.Fragment>
  )
}

export default KycArchiveSearchPage
